import React from 'react';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface HourlyRevenueData {
  hour: string;
  revenue: number;
  covers?: number;
}

interface RevenueTimelineProps {
  data: HourlyRevenueData[];
  loading?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="cc-tooltip">
      <div className="cc-tooltip__label">{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} className="cc-tooltip__row">
          <div className="cc-tooltip__dot" style={{ backgroundColor: p.color || '#6366f1' }} />
          <span className="cc-tooltip__name">{p.name}:</span>
          <span className="cc-tooltip__value">
            {typeof p.value === 'number' && p.value > 100
              ? `$${p.value.toLocaleString()}`
              : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const RevenueTimeline: React.FC<RevenueTimelineProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="cc-chart-container">
        <div className="cc-loading-shimmer" style={{ width: '100%', height: 220, borderRadius: 8 }} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="cc-chart-container">
        <p className="cc-muted-text">No revenue data available</p>
      </div>
    );
  }

  return (
    <div className="cc-chart-container">
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="ccRevGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b44" />
          <XAxis
            dataKey="hour"
            tick={{ fontSize: 9, fill: '#64748b' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 9, fill: '#64748b' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `$${(v / 1000).toFixed(1)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="#6366f1"
            fill="url(#ccRevGrad)"
            strokeWidth={2}
            name="Revenue"
          />
          {data[0]?.covers !== undefined && (
            <Line
              type="monotone"
              dataKey="covers"
              stroke="#06b6d4"
              strokeWidth={2}
              dot={false}
              name="Covers"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueTimeline;
